/**
 * Utgående e-post för kundtoppnod. Ikryssad betyder att mailet går ut.
 */

import React from 'react';
import { useTranslation } from '@/store/languageStore';
import {
  ORGANIZATION_EMAIL_KINDS,
  mergeOrganizationEmailSettings,
  type OrganizationEmailKind,
  type OrganizationEmailSettings,
} from '@/utils/organizationEmailDefaults';

interface Props {
  value: OrganizationEmailSettings | null | undefined;
  onChange: (next: OrganizationEmailSettings) => void;
  /** null eller utelämnad = visa alla. Lista = bara de typer kunden har. */
  visibleKinds?: OrganizationEmailKind[] | null;
}

const OrganizationEmailSettingsPanel: React.FC<Props> = ({ value, onChange, visibleKinds }) => {
  const { t } = useTranslation();
  const settings = mergeOrganizationEmailSettings(value);
  const kinds =
    visibleKinds == null
      ? ORGANIZATION_EMAIL_KINDS
      : ORGANIZATION_EMAIL_KINDS.filter((kind) => visibleKinds.includes(kind));

  const toggle = (kind: OrganizationEmailKind, enabled: boolean) => {
    onChange({ ...settings, [kind]: enabled });
  };

  return (
    <div className="space-y-4 p-4 rounded-lg border border-border bg-bgElevated">
      <div>
        <h3 className="text-base font-semibold text-textDefault mb-1">{t('organizations.emailSettings.title')}</h3>
        <p className="text-sm text-textSecondary">{t('organizations.emailSettings.intro')}</p>
      </div>
      <div className="space-y-2">
        {kinds.map((kind) => (
          <label key={kind} className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings[kind]}
              onChange={(event) => toggle(kind, event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <span>
              <span className="block text-sm font-medium text-textDefault">
                {t(`organizations.emailSettings.kinds.${kind}`)}
              </span>
              <span className="block text-xs text-textTertiary">
                {t(`organizations.emailSettings.kindHelp.${kind}`)}
              </span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default OrganizationEmailSettingsPanel;
